import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Input,
  Option,
  Select,
  Stack,
  styled,
  Typography,
} from '@mui/joy';
import { css } from '@mui/system';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Controller, useForm } from 'react-hook-form';

import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { OrganizationAdminLevelsTable } from '@/components/organizations/table/OrganizationAdminLevelsTable';
import { OrganizationUsersTable } from '@/components/organizations/table/OrganizationUsersTable';
import { I18N_DE } from '@/i18n/de';
import { OrganizationType } from '@/modules/api';
import { backendApiOrganizationsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useOrganizationQuery } from '@/queries/useOrganizationQuery';
import { EntityActive } from '@/types/EntityActive';

const SmallLabel = styled(FormLabel)(
  ({ theme }) => css`
    font-size: 14px;
    font-weight: 500;
    margin-left: ${theme.spacing(2)};
    margin-bottom: 0;
  `,
);

type FormData = {
  name: string;
  type: OrganizationType;
  active: EntityActive;
  email: string;
  postalCode: string;
  city: string;
  street: string;
  phoneNumber: string;
  mobileNumber: string;
};

function OrganizationDetailsRoute() {
  const { data, isFetching } = useOrganizationQuery({
    adminLevels: true,
    users: true,
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>();

  const { mutateAsync, isPending } = useMutation(backendApiOrganizationsUpdateMutation());

  const form = data && (
    <Stack
      gap={2}
      component="form"
      onSubmit={handleSubmit(
        async ({ name, type, active, email, city, postalCode, street, phoneNumber, mobileNumber }) => {
          if (isPending) return;

          await mutateAsync({
            path: { organizationId: data.id },
            body: {
              name,
              type,
              ...(!data?.bemasId && {
                email: email || null,
                postalCode: postalCode || null,
                street: street || null,
                city: city || null,
                phoneNumber: phoneNumber || null,
                mobileNumber: mobileNumber || null,
              }),
              active: active === EntityActive.TRUE,
            },
          });
        },
      )}
    >
      <Typography level="h3">Organisation</Typography>

      <FormControl error={!!errors.name}>
        <FormLabel>Name</FormLabel>
        {data.bemasId ? (
          <Input value={data?.name ?? ''} readOnly />
        ) : (
          <Controller
            control={control}
            name="name"
            defaultValue={data.name || ''}
            render={({ field }) => <Input variant="outlined" {...field} type="text" />}
          />
        )}
        {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
      </FormControl>

      <FormControl error={!!errors.type}>
        <FormLabel>Typ</FormLabel>
        {data.bemasId ? (
          <Input value={I18N_DE.OrganizationType[data.type]} readOnly />
        ) : (
          <Controller
            name="type"
            control={control}
            defaultValue={data.type || ''}
            render={({ field, fieldState }) => (
              <FormControl error={!!fieldState.error}>
                <Select
                  variant="outlined"
                  {...field}
                  value={field.value || null}
                  onChange={(event, value) => {
                    field.onChange(value);
                  }}
                >
                  {Object.values(OrganizationType).map((organizationType) => (
                    <Option key={organizationType} value={organizationType}>
                      {I18N_DE.OrganizationType[organizationType]}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        )}
        {errors.name && <FormHelperText>{errors.name.message}</FormHelperText>}
      </FormControl>

      <FormControl>
        <FormLabel>Status</FormLabel>
        <Controller
          control={control}
          name="active"
          defaultValue={data.active ? EntityActive.TRUE : EntityActive.FALSE}
          render={({ field }) => (
            <RadioWithDescriptionGroup
              {...field}
              options={Object.values(EntityActive)}
              optionLabel={getI18nRadioLabel({
                i18nTitle: I18N_DE.EntityActive,
                i18nDescription: I18N_DE.EntityActiveDescription,
              })}
            />
          )}
        />
      </FormControl>

      <FormControl>
        <FormLabel>{`Kontaktinformationen${!data.bemasId ? ' (optional)' : ''}`}</FormLabel>
        <Grid container spacing={1}>
          <Grid xs={2}>
            <FormControl error={!!errors.postalCode}>
              <SmallLabel>Postleitzahl</SmallLabel>
              {data.bemasId ? (
                <Input value={data.postalCode ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="postalCode"
                  defaultValue={data.postalCode || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="text" />}
                />
              )}
              {errors.postalCode && <FormHelperText>{errors.postalCode.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid xs={10}>
            <FormControl error={!!errors.city}>
              <SmallLabel>Ort</SmallLabel>
              {data.bemasId ? (
                <Input value={data.city ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="city"
                  defaultValue={data.city || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="text" />}
                />
              )}
              {errors.city && <FormHelperText>{errors.city.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <FormControl error={!!errors.street}>
              <SmallLabel>Straße</SmallLabel>
              {data.bemasId ? (
                <Input value={data.street ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="street"
                  defaultValue={data.street || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="text" />}
                />
              )}
              {errors.street && <FormHelperText>{errors.street.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid xs={6}>
            <FormControl error={!!errors.phoneNumber}>
              <SmallLabel>Telefonnummer</SmallLabel>
              {data.bemasId ? (
                <Input value={data.phoneNumber ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="phoneNumber"
                  defaultValue={data.phoneNumber || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="text" />}
                />
              )}
              {errors.phoneNumber && <FormHelperText>{errors.phoneNumber.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid xs={6}>
            <FormControl error={!!errors.mobileNumber}>
              <SmallLabel>Mobiltelefonnummer</SmallLabel>
              {data.bemasId ? (
                <Input value={data.mobileNumber ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="mobileNumber"
                  defaultValue={data.mobileNumber || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="text" />}
                />
              )}
              {errors.mobileNumber && <FormHelperText>{errors.mobileNumber.message}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid xs={12}>
            <FormControl error={!!errors.email}>
              <SmallLabel>E-Mail</SmallLabel>
              {data.bemasId ? (
                <Input value={data.email ?? ''} readOnly />
              ) : (
                <Controller
                  control={control}
                  name="email"
                  defaultValue={data.email || ''}
                  render={({ field }) => <Input variant="outlined" {...field} type="email" />}
                />
              )}
              {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </FormControl>

      <Stack direction="row" justifyContent="flex-end" marginTop={4}>
        <Button type="submit" disabled={isPending} loading={isPending}>
          Speichern
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Grid container spacing={4}>
      <Grid md={6}>{form}</Grid>

      <Grid md={6}>
        <Typography level="h3" marginBottom={6}>
          Zugeordnete Verwaltungsebenen
        </Typography>
        <OrganizationAdminLevelsTable data={data?.adminLevels} isFetching={isFetching} show="adminLevels" />

        <Typography level="h3" my={6}>
          Zugeordnete Benutzer*innen
        </Typography>
        <OrganizationUsersTable
          data={data?.users}
          isFetching={isFetching}
          show="users"
          detailsRoutePath="/administration/organisationen/$organizationId/benutzerinnen/$userId"
        />
      </Grid>
    </Grid>
  );
}

export const Route = createFileRoute('/_authenticated/_management/administration/organisationen/$organizationId/')({
  component: OrganizationDetailsRoute,
});
